/**
 * Bank-statement payment matching (#2897).
 *
 * Paste or enter statement rows, review suggested open invoices, and record
 * selected rows as bank transfers.
 */

import { useEffect, useMemo, useState, type ClipboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { api } from '@/lib/api';
import { translateBillingError } from '@/lib/billingErrors';
import { useNotify } from '@/hooks/useNotify';
import {
  invoiceWorkOrderRef,
  useInvoices,
  useRecordPayment,
  type InvoiceListItem,
  type InvoiceListResponse,
} from '@/hooks/useInvoices';
import { formatCentsAsEuros, formatCentsForDisplay as formatPrice } from '@/utils/money';
import {
  matchPaymentsToInvoices,
  type MatchConfidence,
  type OpenInvoice,
} from './paymentMatching';
import { parseStatementAmountCents, parseStatementText } from './statementPaste';
import { isCollectedExternally } from '@/utils/invoiceCollection';

const OPEN_INVOICE_STATUS = 'sent,partial,overdue';
const INVOICE_PAGE_SIZE = 200;

const CONFIDENCE_LABEL_KEY: Record<MatchConfidence, string> = {
  reference: 'paymentMatching.confidence.reference',
  amount_name: 'paymentMatching.confidence.amountName',
  none: 'paymentMatching.confidence.none',
};

const CONFIDENCE_COLOR: Record<MatchConfidence, 'success' | 'warning' | 'default'> = {
  reference: 'success',
  amount_name: 'warning',
  none: 'default',
};

type RecordState = 'idle' | 'success' | 'error';

interface MatchingRow {
  id: string;
  amountInput: string;
  amountCents: number;
  reference: string;
  payerName: string;
  paymentDate: string;
  invoiceId: string | null;
  selected: boolean;
  userPicked: boolean;
  recordState: RecordState;
  errorMessage: string | null;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function todayIsoDate(): string {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

function amountInputFromCents(cents: number): string {
  return formatCentsAsEuros(cents).replace('.', ',');
}

function toOpenInvoice(item: InvoiceListItem): OpenInvoice {
  const extra = item as InvoiceListItem & { paymentReferenceNumber?: string | null };
  const open: OpenInvoice = {
    id: item.id,
    invoiceNumber: item.invoiceNumber,
    customerName: item.customerName,
    totalCents: item.totalCents,
    paidAmountCents: item.paidAmountCents,
  };
  if (extra.paymentReferenceNumber) {
    open.paymentReferenceNumber = extra.paymentReferenceNumber;
  }
  return open;
}

function outstandingOf(item: InvoiceListItem): number {
  return item.totalCents - item.paidAmountCents;
}

function useOpenInvoicesForMatching(): {
  invoices: InvoiceListItem[];
  isLoading: boolean;
  isError: boolean;
} {
  const first = useInvoices(
    { status: OPEN_INVOICE_STATUS },
    { limit: INVOICE_PAGE_SIZE, offset: 0 },
  );
  const [rest, setRest] = useState<InvoiceListItem[]>([]);
  const firstRows = first.data?.data;
  const total = first.data?.total ?? 0;

  useEffect(() => {
    if (total <= INVOICE_PAGE_SIZE) {
      setRest((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    let cancelled = false;
    void (async () => {
      const collected: InvoiceListItem[] = [];
      for (let offset = INVOICE_PAGE_SIZE; offset < total; offset += INVOICE_PAGE_SIZE) {
        const params = new URLSearchParams({
          status: OPEN_INVOICE_STATUS,
          limit: String(INVOICE_PAGE_SIZE),
          offset: String(offset),
        });
        const page = await api.get<InvoiceListResponse>(`/invoices?${params.toString()}`);
        collected.push(...page.data);
      }
      if (!cancelled) setRest(collected);
    })();
    return () => {
      cancelled = true;
    };
  }, [firstRows, total]);

  const invoices = useMemo(() => {
    const merged = [...(firstRows ?? []), ...rest];
    // Imported invoices are collected in the previous system (#4189).
    return merged.filter((item) => outstandingOf(item) > 0 && !isCollectedExternally(item));
  }, [firstRows, rest]);

  return { invoices, isLoading: first.isLoading, isError: first.isError };
}

function emptyRow(): MatchingRow {
  return {
    id: crypto.randomUUID(),
    amountInput: '',
    amountCents: 0,
    reference: '',
    payerName: '',
    paymentDate: todayIsoDate(),
    invoiceId: null,
    selected: false,
    userPicked: false,
    recordState: 'idle',
    errorMessage: null,
  };
}

export default function PaymentMatchingPage() {
  const { t } = useTranslation('invoices');
  const navigate = useNavigate();
  const notify = useNotify();
  const recordPayment = useRecordPayment();
  const { invoices, isLoading, isError } = useOpenInvoicesForMatching();

  const [pasteText, setPasteText] = useState('');
  const [rows, setRows] = useState<MatchingRow[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [recording, setRecording] = useState(false);

  const openInvoices = useMemo(() => invoices.map(toOpenInvoice), [invoices]);
  const invoiceById = useMemo(
    () => new Map(invoices.map((item) => [item.id, item])),
    [invoices],
  );

  const statementRows = useMemo(
    () =>
      rows.map((row) => ({
        id: row.id,
        amountCents: row.amountCents,
        reference: row.reference.trim() ? row.reference.trim() : null,
        payerName: row.payerName.trim() ? row.payerName.trim() : null,
      })),
    [rows],
  );

  const matches = useMemo(
    () => matchPaymentsToInvoices(statementRows, openInvoices),
    [statementRows, openInvoices],
  );
  const matchByRowId = useMemo(
    () => new Map(matches.map((match) => [match.rowId, match])),
    [matches],
  );

  useEffect(() => {
    setRows((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (row.userPicked || row.recordState === 'success') return row;
        const match = matchByRowId.get(row.id);
        const invoiceId = match?.invoiceId ?? null;
        const selected = match?.confidence === 'reference';
        if (row.invoiceId === invoiceId && row.selected === selected) return row;
        changed = true;
        return { ...row, invoiceId, selected };
      });
      return changed ? next : prev;
    });
  }, [matchByRowId]);

  const applyParsed = (text: string) => {
    const parsed = parseStatementText(text);
    const today = todayIsoDate();
    setRows(
      parsed.map((item, index) => ({
        id: `stmt-${String(index)}-${String(item.amountCents)}`,
        amountInput: amountInputFromCents(item.amountCents),
        amountCents: item.amountCents,
        reference: item.reference ?? '',
        payerName: item.payerName ?? '',
        paymentDate: item.paymentDate ?? today,
        invoiceId: null,
        selected: false,
        userPicked: false,
        recordState: 'idle' as const,
        errorMessage: null,
      })),
    );
  };

  const handlePaste = (event: ClipboardEvent<HTMLElement>) => {
    const text = event.clipboardData.getData('text/plain');
    if (!text.includes('\n') && !text.includes('\t') && !text.includes(';')) return;
    event.preventDefault();
    setPasteText(text);
    applyParsed(text);
  };

  const updateRow = (id: string, patch: Partial<MatchingRow>) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const selectedRows = rows.filter(
    (row) =>
      row.selected
      && row.recordState !== 'success'
      && row.invoiceId
      && row.amountCents > 0,
  );
  const selectedSumCents = selectedRows.reduce((sum, row) => sum + row.amountCents, 0);

  const handleRecord = async () => {
    setConfirmOpen(false);
    setRecording(true);
    let ok = 0;
    let failed = 0;
    for (const row of selectedRows) {
      const invoiceId = row.invoiceId;
      if (!invoiceId) continue;
      const invoice = invoiceById.get(invoiceId);
      try {
        const body = {
          invoiceId,
          amountCents: row.amountCents,
          paymentMethod: 'bank_transfer' as const,
          ...invoiceWorkOrderRef(invoice?.bookingId),
          ...(row.reference.trim() ? { paymentReference: row.reference.trim() } : {}),
          ...(row.paymentDate ? { paymentDate: new Date(row.paymentDate).toISOString() } : {}),
        };
        await recordPayment.mutateAsync(body);
        ok += 1;
        updateRow(row.id, {
          recordState: 'success',
          selected: false,
          errorMessage: null,
          userPicked: true,
        });
      } catch (error) {
        failed += 1;
        updateRow(row.id, {
          recordState: 'error',
          selected: true,
          userPicked: true,
          errorMessage: translateBillingError(error, t),
        });
      }
    }
    setRecording(false);
    if (failed === 0 && ok > 0) {
      notify.success(t('paymentMatching.success'), t('paymentMatching.successDesc', { count: ok }));
    } else if (ok > 0) {
      notify.warning(
        t('paymentMatching.partialSuccess'),
        t('paymentMatching.partialSuccessDesc', { ok, failed }),
      );
    } else if (failed > 0) {
      notify.error(t('paymentMatching.rowError'));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, sm: 3 } }} data-testid="payment-matching-page">
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/invoices')} sx={{ mb: 2 }} size="small">
        {t('detailPage.backToList')}
      </Button>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        {t('paymentMatching.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('paymentMatching.subtitle')}
      </Typography>

      {isError ? <Alert severity="error" sx={{ mb: 2 }}>{t('paymentMatching.openInvoicesError')}</Alert> : null}

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          label={t('paymentMatching.pasteLabel')}
          helperText={t('paymentMatching.pasteHelp')}
          value={pasteText}
          onChange={(event) => setPasteText(event.target.value)}
          onPaste={handlePaste}
          multiline
          minRows={4}
          fullWidth
          data-testid="statement-paste"
          slotProps={{ htmlInput: { 'data-testid': 'statement-paste-input' } }}
        />
        <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            onClick={() => applyParsed(pasteText)}
            data-testid="import-statement"
          >
            {t('paymentMatching.importRows')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setRows((prev) => [...prev, emptyRow()])}
          >
            {t('paymentMatching.addRow')}
          </Button>
        </Stack>
      </Paper>

      {isLoading ? (
        <CircularProgress size={24} />
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">{t('paymentMatching.noRows')}</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">{t('paymentMatching.selectRow')}</TableCell>
                <TableCell>{t('paymentMatching.paymentDate')}</TableCell>
                <TableCell>{t('paymentMatching.amount')}</TableCell>
                <TableCell>{t('paymentMatching.reference')}</TableCell>
                <TableCell>{t('paymentMatching.payerName')}</TableCell>
                <TableCell>{t('paymentMatching.invoice')}</TableCell>
                <TableCell>{t('paymentMatching.outstanding')}</TableCell>
                <TableCell>{t('paymentMatching.confidence.label')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const match = matchByRowId.get(row.id);
                const confidence: MatchConfidence = match?.confidence ?? 'none';
                const invoice = row.invoiceId ? invoiceById.get(row.invoiceId) : undefined;
                const outstanding = invoice ? outstandingOf(invoice) : null;
                const isPartial = match?.isPartial === true
                  || (outstanding != null && row.amountCents > 0 && row.amountCents < outstanding);
                const recorded = row.recordState === 'success';
                return (
                  <TableRow key={row.id} data-testid={`matching-row-${row.id}`}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={row.selected && !recorded}
                        disabled={recorded || recording}
                        onChange={(event) =>
                          updateRow(row.id, {
                            selected: event.target.checked,
                            userPicked: true,
                          })}
                        slotProps={{ input: { 'aria-label': t('paymentMatching.selectRow') } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="date"
                        size="small"
                        value={row.paymentDate}
                        disabled={recorded}
                        onChange={(event) =>
                          updateRow(row.id, { paymentDate: event.target.value, userPicked: true })}
                        slotProps={{ inputLabel: { shrink: true } }}
                        aria-label={t('paymentMatching.paymentDate')}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.amountInput}
                        disabled={recorded}
                        onChange={(event) => {
                          const amountInput = event.target.value;
                          const parsed = parseStatementAmountCents(amountInput);
                          updateRow(row.id, {
                            amountInput,
                            amountCents: parsed != null && parsed > 0 ? parsed : 0,
                            userPicked: true,
                          });
                        }}
                        aria-label={t('paymentMatching.amount')}
                        sx={{ minWidth: 110 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.reference}
                        disabled={recorded}
                        onChange={(event) =>
                          updateRow(row.id, { reference: event.target.value, userPicked: true })}
                        aria-label={t('paymentMatching.reference')}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.payerName}
                        disabled={recorded}
                        onChange={(event) =>
                          updateRow(row.id, { payerName: event.target.value, userPicked: true })}
                        aria-label={t('paymentMatching.payerName')}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 260 }}>
                      <Autocomplete
                        size="small"
                        options={invoices}
                        value={invoice ?? null}
                        disabled={recorded}
                        onChange={(_event, value) =>
                          updateRow(row.id, {
                            invoiceId: value?.id ?? null,
                            selected: value != null,
                            userPicked: true,
                          })}
                        getOptionLabel={(option) =>
                          `${option.invoiceNumber} · ${option.customerName}`}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                          <TextField {...params} label={t('paymentMatching.invoiceSearch')} />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      {outstanding != null ? formatPrice(outstanding) : '—'}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          label={t(CONFIDENCE_LABEL_KEY[confidence])}
                          color={CONFIDENCE_COLOR[confidence]}
                        />
                        {isPartial && row.invoiceId ? (
                          <Chip size="small" label={t('paymentMatching.partial')} color="warning" variant="outlined" />
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {row.recordState === 'success' ? (
                        <Chip size="small" color="success" label={t('paymentMatching.recorded')} />
                      ) : null}
                      {row.recordState === 'error' && row.errorMessage ? (
                        <Typography variant="caption" color="error">{row.errorMessage}</Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {rows.length > 0 ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Typography variant="body2">
            {t('paymentMatching.selectedCount', { count: selectedRows.length })}
            {' · '}
            {t('paymentMatching.selectedSum', { amount: formatPrice(selectedSumCents) })}
          </Typography>
          <Button
            variant="contained"
            disabled={selectedRows.length === 0 || recording}
            onClick={() => setConfirmOpen(true)}
            data-testid="record-selected"
          >
            {t('paymentMatching.recordSelected')}
          </Button>
        </Stack>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        title={t('paymentMatching.confirmTitle')}
        message={t('paymentMatching.confirmMessage', {
          count: selectedRows.length,
          amount: formatPrice(selectedSumCents),
        })}
        confirmText={t('paymentMatching.recordSelected')}
        onConfirm={() => {
          void handleRecord();
        }}
        onCancel={() => setConfirmOpen(false)}
        loading={recording}
      />
    </Box>
  );
}
